<template>
  <DeleteConfirmation
    :isOpen="showDeleteConfirmation"
    :handleClose="handleCloseDeleteConfirmation"
    :handleDelete="handleDelete"
  />
  <ComponentPreviewModal
    :open="showComponentPreviewModal"
    :close="handleCloseComponentPreviewModal"
    :shortCode="`[${getComponentName(componentId)
      .toLowerCase()
      .replaceAll(' ', '_')} name=${data.name}]`"
  />
  <tr class="border-b-2 border-gray-50">
    <td
      class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
    >
      {{ data.id }}
    </td>
    <td
      class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
    >
      <div v-if="!shouldEdit">
        <p @click="handleToggleShouldEdit">
          {{ changedName || formattedComponentName }}
        </p>
      </div>
      <div v-else class="flex items-center">
        <input
          v-model="newInstanceName"
          type="text"
          class="w-[250px] border-0 px-1 py-1 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
        />
        <button
          class="ml-2 cursor-pointer rounded p-1/2 bg-green-300"
          @click="updateInstanceName"
        >
          <IconCheck />
        </button>
        <button
          class="ml-2 cursor-pointer rounded p-1/2 bg-red-300"
          @click="handleToggleShouldEdit"
        >
          <IconCross />
        </button>
      </div>
    </td>
    <td
      @mouseover="handleMouseOverDesc"
      @mouseleave="handleMouseLeaveDesc"
      :class="!data.description && 'text-gray-400'"
      class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4"
    >
      <transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="transform opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="transform opacity-0"
        mode="out-in"
      >
        <input
          v-model="description"
          v-if="showDescInput"
          type="text"
          class="block w-2/3 h-[30px] py-2 px-3 mr-2 bg-white text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
        <span v-else>{{
          description
            ? description
            : data.description
            ? data.description
            : "No description for this instance"
        }}</span>
      </transition>
    </td>

    <td
      class="border-t-0 flex items-center justify-end px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right"
    >
      <button
        :disabled="checkCopyExist()"
        @click="handleCopyInstance"
        type="button"
        :class="
          !checkCopyExist()
            ? 'bg-blue-500 active:bg-blue-600 '
            : 'bg-gray-200 active:bg-gray-300 cursor-not-allowed'
        "
        class="flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <IconDocumentDuplicate v-if="!copyLoading" class="w-4 h-4 m-0" />
        <IconSync v-if="copyLoading" class="w-4 h-4 animate-spin" />
      </button>
      <button
        @click="handleOpenComponentPreviewModal"
        type="button"
        class="bg-blue-500 active:bg-blue-600 flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <IconPreview class="w-4 h-4 m-0" />
      </button>
      <button
        @click="handleCopyShortCode"
        :class="
          shortCodeCopied
            ? 'bg-green-500 active:bg-green-600'
            : 'bg-blue-500 active:bg-blue-600'
        "
        type="button"
        class="bg-blue-500 active:bg-blue-600 flex items-center text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <IconCheck v-if="shortCodeCopied" class="w-4 h-4 m-0" />
        <IconCode v-else class="w-4 h-4 m-0" />
      </button>
      <button
        @click="handleRedirect(data)"
        class="bg-amber-400 text-white active:bg-amber-500 font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
        type="button"
      >
        <IconPencil class="w-4 h-4 m-0" :fill="'#ffffff'" />
      </button>
      <button
        :disabled="isCategoryComp"
        @click="handleOpenDeleteConfirmation"
        :class="
          isCategoryComp
            ? 'bg-gray-200 active:bg-gray-300'
            : 'bg-gray-400 active:bg-gray-500'
        "
        class="text-white font-bold uppercase text-xs px-4 py-2 rounded shadow hover:shadow-md outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      >
        <div class="flex items-center justify-center">
          <IconDelete v-if="!getDeleteLoading[id]" class="w-4 h-4" />
          <IconSync v-if="getDeleteLoading[id]" class="w-4 h-4 animate-spin" />
        </div>
      </button>
    </td>
  </tr>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "underscore";
import * as Actions from "@/store/actions.type";
import ABILogo from "@/assets/imgs/logo.png";
import DeleteConfirmation from "@/components/Shared/Modal/DeleteConfirmation";
import ComponentPreviewModal from "@/components/Shared/Modal/ComponentPreviewModal";
import IconDelete from "@/assets/icons/IconDelete";
import IconDocumentDuplicate from "@/assets/icons/IconDocumentDuplicate";
import IconSync from "@/assets/icons/IconSync";
import IconCode from "@/assets/icons/IconCode";
import IconCheck from "@/assets/icons/IconCheck";
import IconPencil from "@/assets/icons/IconPencil";
import IconPreview from "@/assets/icons/IconPreview";
import IconCross from "@/assets/icons/IconCross";

export default {
  name: "InstanceTableRow",
  components: {
    ComponentPreviewModal,
    IconDelete,
    IconSync,
    IconCode,
    IconCheck,
    IconCross,
    IconPencil,
    IconPreview,
    IconDocumentDuplicate,
    DeleteConfirmation
  },
  data() {
    return {
      ABILogo,
      shortCodeCopied: false,
      showComponentPreviewModal: false,
      showDeleteConfirmation: false,
      showDescInput: false,
      copyLoading: false,
      newInstanceName: "",
      shouldEdit: false,
      changedName: ""
    };
  },
  props: {
    data: {
      type: [Array, Object],
      required: true
    },
    id: {
      type: Number,
      required: true
    },
    draftList: {
      type: Array,
      required: true
    },
    componentId: {
      type: String,
      required: true
    }
  },
  created() {
    this.debounceFn = _.debounce(() => {
      this.$store.dispatch(Actions.UPDATE_INSTANCE_DESCRIPTION, {
        region: this.getRegion,
        instanceId: this.data.id,
        description: this.description
      });
    }, 500);
  },
  mounted() {
    this.description = this.data.description;
    this.newInstanceName = this.formattedComponentName;
  },
  computed: {
    description: {
      get: function () {
        return this.getInstanceDescription[this.data.id];
      },
      set: function (val) {
        this.$store.dispatch(Actions.SET_DESCRIPTION_CACHE, {
          description: val,
          instanceId: this.data.id
        });
        if (this.description !== this.data.description) {
          this.debounceFn();
        }
      }
    },
    draftId() {
      if (this.draftList.length) {
        return this.draftList.find((item) => item.name === this.data.name)?.id;
      } else {
        return "";
      }
    },
    formData() {
      const foundItem = this.getInstanceInfo.find((i) => i.id === this.data.id);
      return foundItem ? foundItem.attributes : {};
    },
    formattedComponentName() {
      const splitName = this.data.name.split("|");
      return splitName[1];
    },
    categoryNameList() {
      if (this.categoryInfo.length) {
        return this.categoryInfo.map((item) => item.name);
      } else {
        return [];
      }
    },
    isCategoryComp() {
      if (this.componentId === this.getComponentId("abi_content_text_block")) {
        let keywords = ["faqs", "assistance", "range", "overview"];
        if (
          keywords.includes(this.formattedComponentName.split("_")[0]) &&
          this.categoryNameList.includes(
            this.formattedComponentName.split("_")[1]
          )
        ) {
          return true;
        } else {
          return false;
        }
      } else if (
        this.componentId ===
        this.getComponentId("abi_category" || "abi_category_faq")
      ) {
        if (this.categoryNameList.includes(this.formattedComponentName)) {
          return true;
        } else {
          return false;
        }
      }
      return false;
    },
    ...mapGetters([
      "categoryInfo",
      "getAllComponent",
      "getDeleteLoading",
      "componentErrors",
      "getInstanceDescription",
      "getRegion",
      "getInstanceInfo",
      "getInstanceDraft",
      "currentUser"
    ])
  },
  methods: {
    updateInstanceName() {
      if (this.newInstanceName) {
        this.$store
          .dispatch(Actions.UPDATE_INSTANCE_NAME, {
            region: this.getRegion,
            componentId: this.componentId,
            instanceId: this.data.id,
            draftId: this.draftId,
            instanceName: this.newInstanceName
          })
          .then(() => {
            this.changedName = this.newInstanceName;
          });
      } else {
        this.$store.dispatch(
          Actions.ADD_COMPONENT_ERRORS,
          "Instance name cannot be empty!"
        );
      }
      this.handleToggleShouldEdit();
    },
    handleToggleShouldEdit() {
      this.shouldEdit = !this.shouldEdit;
    },
    handleCopyShortCode() {
      navigator.clipboard.writeText(
        `[${this.getComponentName(this.componentId)
          .toLowerCase()
          .replaceAll(" ", "_")} name=${this.data.name}]`
      );
      this.shortCodeCopied = true;
      setTimeout(() => {
        this.shortCodeCopied = false;
      }, 3000);
    },
    handleCopyInstance() {
      if (this.getComponentName(this.componentId) === "abi_image_slider") {
        const formDataCopy = JSON.parse(JSON.stringify(this.formData));
        let slides = formDataCopy.slides.map((i) => {
          return { instance: i };
        });
        formDataCopy.slides = slides;
        this.copyLoading = true;
        this.$store
          .dispatch(Actions.COPY_SLIDER_INSTANCE, {
            region: this.getRegion,
            formData: { slider: formDataCopy },
            componentId: this.componentId,
            title: this.formattedComponentName,
            user: this.currentUser.email
          })
          .then(() => {
            this.copyLoading = false;
            this.$store.dispatch(Actions.FETCH_COMPONENT_DATA, {
              region: this.getRegion,
              componentId: this.componentId
            });
          });
      } else {
        this.copyLoading = true;
        this.$store
          .dispatch(Actions.COPY_COMPONENT_DATA, {
            formData: this.formData,
            region: this.getRegion,
            componentId: this.componentId,
            title: this.formattedComponentName,
            user: this.currentUser.email
          })
          .then(() => {
            this.copyLoading = false;
            this.$store.dispatch(Actions.FETCH_COMPONENT_DATA, {
              region: this.getRegion,
              componentId: this.componentId
            });
          });
      }
    },
    getComponentId(componentName) {
      if (this.getAllComponent.find((item) => item.name === componentName)) {
        return this.getAllComponent.find((item) => item.name === componentName)
          .id;
      } else {
        return null;
      }
    },
    getComponentName(componentId) {
      if (this.getAllComponent.find((item) => item.id === componentId)) {
        return this.getAllComponent.find((item) => item.id === componentId)
          .name;
      } else {
        return null;
      }
    },
    handleDelete() {
      this.$store.dispatch(Actions.DELETE_COMPONENT_DATA, {
        region: this.getRegion,
        instanceId: this.data.id,
        draftId: this.draftId,
        index: this.id
      });
      this.showDeleteConfirmation = false;
    },
    handleRedirect(data) {
      this.$store.dispatch(Actions.CHANGE_OPERATION_MODE, {
        operation: "update"
      });
      this.$router.push({
        path: `/component/edit/${this.componentId}/${data.id}`
      });
    },
    handleOpenComponentPreviewModal() {
      this.showComponentPreviewModal = true;
    },
    handleCloseComponentPreviewModal() {
      this.showComponentPreviewModal = false;
    },
    handleOpenDeleteConfirmation() {
      this.showDeleteConfirmation = true;
    },
    handleCloseDeleteConfirmation() {
      this.showDeleteConfirmation = false;
    },
    handleMouseOverDesc() {
      this.showDescInput = true;
    },
    handleMouseLeaveDesc() {
      this.showDescInput = false;
    },
    checkCopyExist() {
      let copyExist = false;
      this.getInstanceInfo.forEach((item) => {
        if (
          item.instance_name.split("|")[1] ===
          this.formattedComponentName + "_copy"
        ) {
          copyExist = true;
        }
      });
      return copyExist;
    }
  }
};
</script>
